/**
 * Create Search Trigger Button
 * Optional floating trigger button for opening search.
 *
 * Features:
 *  - Platform-aware keyboard shortcut (⌘K on Mac, Ctrl+K elsewhere, hidden on touch)
 *  - Light / dark / auto color scheme
 *  - CSS custom properties for theme developer overrides
 *  - prefers-reduced-motion, prefers-contrast: high, focus-visible
 *  - Responds to system color scheme changes in real time
 */

import type { ColorScheme, LoaderConfig, LoaderTheme } from './types';

// ---------------------------------------------------------------------------
// Position presets
// ---------------------------------------------------------------------------
const POSITION_STYLES: Record<string, string> = {
  'top-right': 'top: 20px; right: 20px;',
  'top-left': 'top: 20px; left: 20px;',
  'bottom-right': 'bottom: 20px; right: 20px;',
  'bottom-left': 'bottom: 20px; left: 20px;',
};

// ---------------------------------------------------------------------------
// Default color palettes
// ---------------------------------------------------------------------------
const DEFAULT_COLORS = {
  light: {
    background: '#ffffff',
    textColor: '#202124',
    borderColor: 'rgba(0, 0, 0, 0.1)',
  },
  dark: {
    background: '#0a0a0a',
    textColor: '#e8eaed',
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
};

// ---------------------------------------------------------------------------
// Platform detection helpers
// ---------------------------------------------------------------------------
function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '');
}

function isTouchOnly(): boolean {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window && !window.matchMedia('(pointer: fine)').matches;
}

// ---------------------------------------------------------------------------
// Color scheme helpers
// ---------------------------------------------------------------------------
function getSystemColorScheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getEffectiveColorScheme(colorScheme?: ColorScheme): 'light' | 'dark' {
  if (colorScheme === 'light') return 'light';
  if (colorScheme === 'dark') return 'dark';
  return getSystemColorScheme();
}

// ---------------------------------------------------------------------------
// Resolve trigger colors from theme config
// ---------------------------------------------------------------------------
function getTriggerColors(theme?: LoaderTheme): {
  background: string;
  textColor: string;
  borderColor: string;
} {
  const effectiveScheme = getEffectiveColorScheme(theme?.colorScheme);
  const isDark = effectiveScheme === 'dark';
  const defaults = isDark ? DEFAULT_COLORS.dark : DEFAULT_COLORS.light;
  const trigger = theme?.trigger;

  const background = isDark
    ? trigger?.darkBackground || trigger?.background || defaults.background
    : trigger?.background || defaults.background;

  const textColor = isDark
    ? trigger?.darkTextColor || trigger?.textColor || defaults.textColor
    : trigger?.textColor || defaults.textColor;

  const borderColor = isDark
    ? trigger?.darkBorderColor || trigger?.borderColor || defaults.borderColor
    : trigger?.borderColor || defaults.borderColor;

  return { background, textColor, borderColor };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
export interface TriggerButtonAPI {
  show: () => void;
  hide: () => void;
  destroy: () => void;
  updateColors: (theme?: LoaderTheme) => void;
}

export function createTrigger(config: LoaderConfig, onClick: () => void): TriggerButtonAPI | null {
  if (config.embedMode === 'inline') {
    return null;
  }
  if (!config.showTrigger) {
    return null;
  }

  const position = config.triggerPosition || 'top-right';
  const isMac = isMacPlatform();
  const touchOnly = isTouchOnly();

  // ------- Create button element -------
  const button = document.createElement('button');
  button.className = 'savanto-search-trigger';
  button.setAttribute('data-sv-component', 'search-trigger');
  button.setAttribute('aria-label', 'Open search');
  button.setAttribute('type', 'button');
  button.setAttribute('role', 'search');

  // ------- Inject scoped styles once -------
  if (!document.getElementById('sv-trigger-styles')) {
    const style = document.createElement('style');
    style.id = 'sv-trigger-styles';
    style.textContent = `
      .savanto-search-trigger {
        position: fixed;
        ${POSITION_STYLES[position] || POSITION_STYLES['top-right']}
        z-index: 999998;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 16px;
        background: var(--sv-trigger-bg);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        border: 1px solid var(--sv-trigger-border);
        border-radius: 12px;
        color: var(--sv-trigger-color);
        font-size: 14px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
        cursor: pointer;
        transition: background 150ms ease, border-color 150ms ease, opacity 150ms ease, transform 150ms ease;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
        line-height: 1;
        white-space: nowrap;
        -webkit-appearance: none;
        appearance: none;
        box-sizing: border-box;
      }
      .savanto-search-trigger:hover {
        opacity: 0.9;
        transform: scale(1.02);
      }
      .savanto-search-trigger:focus { outline: none; }
      .savanto-search-trigger:focus-visible {
        outline: 2px solid rgba(0, 132, 255, 0.4);
        outline-offset: 2px;
      }
      @media (prefers-reduced-motion: reduce) {
        .savanto-search-trigger { transition: none; }
        .savanto-search-trigger:hover { transform: none; }
      }
      @media (prefers-contrast: high) {
        .savanto-search-trigger {
          --sv-trigger-bg: transparent;
          --sv-trigger-border: currentColor;
          box-shadow: none;
        }
      }
      .savanto-search-trigger__icon { flex-shrink: 0; }
      .savanto-search-trigger__kbd {
        padding: 2px 6px;
        background: var(--sv-trigger-border);
        border-radius: 4px;
        font-size: 11px;
        font-weight: 500;
        font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
        color: inherit;
        opacity: 0.7;
        line-height: 1.3;
      }
      @media print { .savanto-search-trigger { display: none !important; } }
    `;
    document.head.appendChild(style);
  }

  // ------- Apply dynamic CSS custom properties -------
  const applyColors = (c: ReturnType<typeof getTriggerColors>) => {
    button.style.setProperty('--sv-trigger-bg', c.background);
    button.style.setProperty('--sv-trigger-color', c.textColor);
    button.style.setProperty('--sv-trigger-border', c.borderColor);
  };

  applyColors(getTriggerColors(config.theme));

  // `theme.trigger.iconUrl` is merchant-supplied — build the <img> via
  // DOM APIs (not string-templated HTML) and gate the scheme.
  const SAFE_IMG_SCHEMES = new Set(['http:', 'https:']);
  const SAFE_IMG_DATA_RE = /^data:image\/(png|jpe?g|gif|webp|svg\+xml);/i;
  const isSafeIconUrl = (raw: string): boolean => {
    // biome-ignore lint/suspicious/noControlCharactersInRegex: matches the HTML URL-attribute leading-control-char strip
    const trimmed = raw.replace(/^[\x00-\x1F\x20]+/, '');
    if (SAFE_IMG_DATA_RE.test(trimmed)) return true;
    if (trimmed.startsWith('//') || trimmed.startsWith('/')) return true;
    try {
      const parsed = new URL(trimmed, window.location.href);
      return SAFE_IMG_SCHEMES.has(parsed.protocol.toLowerCase());
    } catch {
      return false;
    }
  };

  const createIconNode = (): HTMLElement => {
    const iconUrl = config.theme?.trigger?.iconUrl;
    if (iconUrl && isSafeIconUrl(iconUrl)) {
      const img = document.createElement('img');
      img.src = iconUrl;
      img.alt = '';
      img.width = 20;
      img.height = 20;
      img.style.flexShrink = '0';
      img.style.objectFit = 'contain';
      img.style.borderRadius = '4px';
      return img;
    }
    // Default magnifier SVG — literal markup, no merchant input flows in.
    const wrapper = document.createElement('span');
    wrapper.innerHTML =
      '<svg class="savanto-search-trigger__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>';
    const svg = wrapper.firstElementChild as HTMLElement | null;
    return svg ?? wrapper;
  };

  // ------- Build inner content -------
  const updateContent = (c: ReturnType<typeof getTriggerColors>) => {
    const icon = createIconNode();

    const label = document.createElement('span');
    label.textContent = 'Search';

    const children: (Node | string)[] = [icon, label];

    if (!touchOnly) {
      const kbd = document.createElement('kbd');
      kbd.className = 'savanto-search-trigger__kbd';
      if (isMac) {
        const cmd = document.createElement('span');
        cmd.style.fontSize = '10px';
        cmd.textContent = '\u2318';
        kbd.append(cmd, 'K');
      } else {
        kbd.textContent = 'Ctrl+K';
      }
      children.push(kbd);
    }

    button.replaceChildren(...children);
    applyColors(c);
  };

  updateContent(getTriggerColors(config.theme));

  // ------- Click handler -------
  button.addEventListener('click', onClick);

  document.body.appendChild(button);

  // ------- System color scheme watcher -------
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const handleSchemeChange = () => {
    const newColors = getTriggerColors(config.theme);
    updateContent(newColors);
  };
  mediaQuery.addEventListener('change', handleSchemeChange);

  // ------- Return public API -------
  return {
    show: () => {
      button.style.display = 'flex';
    },
    hide: () => {
      button.style.display = 'none';
    },
    destroy: () => {
      mediaQuery.removeEventListener('change', handleSchemeChange);
      button.remove();
    },
    updateColors: (theme?: LoaderTheme) => {
      const newColors = getTriggerColors(theme);
      updateContent(newColors);
    },
  };
}
